import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

export type StatusBadge = {
  label: string;
  tone: "success" | "warning" | "neutral";
};

const badgeSx: Record<StatusBadge["tone"], { color: string; bgcolor: string }> = {
  success: { color: "#1B5E20", bgcolor: "#E8F5E9" },
  warning: { color: "#E65100", bgcolor: "#FFF3E0" },
  neutral: { color: "#455A64", bgcolor: "#ECEFF1" },
};

export const BankBrandMark: React.FunctionComponent = () => (
  <Box
    aria-label="Bank"
    sx={{
      width: 32,
      height: 20,
      minWidth: 32,
      maxWidth: 32,
      minHeight: 20,
      maxHeight: 20,
      lineHeight: "20px",
      boxSizing: "border-box",
      borderRadius: 0.5,
      bgcolor: "#37474F",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 8,
      fontWeight: 800,
      letterSpacing: 0.2,
      flexShrink: 0,
    }}
  >
    BANK
  </Box>
);

export const AchBrandMark: React.FunctionComponent = () => (
  <Box
    aria-label="ACH"
    sx={{
      width: 32,
      height: 20,
      minWidth: 32,
      maxWidth: 32,
      minHeight: 20,
      maxHeight: 20,
      lineHeight: "20px",
      boxSizing: "border-box",
      borderRadius: 0.5,
      bgcolor: "#0D47A1",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 9,
      fontWeight: 800,
      letterSpacing: 0.2,
      flexShrink: 0,
    }}
  >
    ACH
  </Box>
);

export const paymentStatusBadge = (status?: string | null): StatusBadge => {
  const key = (status || "").trim().toUpperCase();
  if (key === "PENDING" || key === "NEW") {
    return { label: "Pending", tone: "warning" };
  }
  if (key === "VERIFIED") {
    return { label: "Verified", tone: "success" };
  }
  return { label: "Approved", tone: "success" };
};

type MethodAccountCardProps = {
  title: string;
  brand: React.ReactNode;
  badges: StatusBadge[];
  details: { label: string; value?: string | null }[];
  extra?: React.ReactNode;
  onEdit: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteDisabledReason?: string;
  deleting?: boolean;
  setDefaultDisabled?: boolean;
  setDefaultDisabledReason?: string;
};

const ActionButton: React.FunctionComponent<{
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  color?: "inherit" | "error";
}> = ({ label, icon, onClick, disabled, disabledReason, color = "inherit" }) => {
  const button = (
    <Button
      size="small"
      variant="text"
      color={color}
      disabled={disabled}
      startIcon={icon}
      onClick={onClick}
      sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
    >
      {label}
    </Button>
  );

  if (!disabled || !disabledReason) {
    return button;
  }

  return (
    <Tooltip title={disabledReason}>
      <span>{button}</span>
    </Tooltip>
  );
};

const MethodAccountCard: React.FunctionComponent<MethodAccountCardProps> = ({
  title,
  brand,
  badges,
  details,
  extra,
  onEdit,
  onDelete,
  deleteDisabled,
  deleteDisabledReason,
  deleting,
  setDefaultDisabled = true,
  setDefaultDisabledReason,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 0,
      display: "flex",
      flexDirection: "column",
      minWidth: 280,
      borderRadius: 2,
      overflow: "hidden",
      borderColor: "rgba(0, 0, 0, 0.12)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2.5, pb: 2 }}>
      {brand}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "flex-end" }}>
        {badges.map((badge) => (
          <Box
            key={badge.label}
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.6,
              ...badgeSx[badge.tone],
            }}
          >
            {badge.label}
          </Box>
        ))}
      </Box>
    </Box>

    <Divider />

    <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
      {details
        .filter((row) => row.value)
        .map((row) => (
          <Box
            key={row.label}
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      {extra}
    </Box>

    <Divider />

    <Box
      sx={{
        px: 1.5,
        py: 1,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <ActionButton
        label="Set as Default"
        icon={<StarOutlineIcon />}
        disabled={setDefaultDisabled}
        disabledReason={setDefaultDisabledReason}
      />
      <ActionButton label="Edit" icon={<EditOutlinedIcon />} onClick={onEdit} />
      <ActionButton
        label={deleting ? "Deleting…" : "Delete"}
        icon={<DeleteOutlineIcon />}
        color="error"
        onClick={onDelete}
        disabled={deleteDisabled || deleting || !onDelete}
        disabledReason={deleteDisabledReason}
      />
    </Box>
  </Paper>
);

export default MethodAccountCard;
